/* ==========================================================================
   we-pdp-variants.js - New PDP variant selection foundation.
   --------------------------------------------------------------------------
   The single authoritative resolver for "which Shopify variant is
   selected" on this PDP. Previously this lived half in we-pdp-buybox.liquid
   (hardcoded Dosage/Size/Pack of markup) and half in we-pdp.js (a
   DOM-scraping resolveVariant() added only so the Gallery had a variant.id
   to react to). Neither read Shopify's actual option structure, so every
   product this theme will ever show this template on had to happen to have
   exactly those three named options in that order.

   This file owns, generically, for however many option groups the current
   product actually has (Shopify caps every product at 3, never more):
     - reading which value is selected per option position from the DOM
       (we-pdp-buybox.liquid already renders the correct initial state from
       product.selected_or_first_available_variant, itself already
       ?variant=-aware and safely falling back for an invalid id - nothing
       here needs to re-parse the URL on load, only keep it in sync after);
     - resolving the matching real Shopify variant;
     - the Part 4 availability rule (can this value pair with the OTHER
       groups' current selections to form an available variant - not "is
       the full current combination available");
     - reflecting selected/unavailable state and the option-label
       text back into the DOM;
     - keeping the URL's ?variant= in sync (history.replaceState - see the
       "History" section below for why);
     - dispatching "wep:variant-change" - the exact event name and
       {detail: {variantId}} shape the Gallery (we-pdp-gallery.js) already
       listens for, now with one real producer instead of we-pdp.js's old
       DOM-scraping one, plus an additive detail.variant for any future
       listener (Pricing, Purchase) that needs more than just the id.

   Own idempotent bind (window.WePdpVariants), own delegated listener - same
   constraints as we-pdp.js and we-pdp-gallery.js.
   ========================================================================== */
(function () {
  'use strict';

  if (window.WePdpVariants && window.WePdpVariants.bound) {
    window.WePdpVariants.refresh();
    return;
  }

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function qs(sel, ctx) {
    return (ctx || document).querySelector(sel);
  }

  function qsa(sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  }

  /* ------------------------------------------------------------------------
     Variant data + resolution
     ------------------------------------------------------------------------ */
  var variants = [];
  var variantsByKey = {};
  var optionCount = 0;

  function optionKey(options) {
    return options.join('');
  }

  function loadVariants() {
    var script = qs('[data-wep-variant-index]');
    if (!script) return;
    try {
      variants = JSON.parse(script.textContent) || [];
    } catch (e) {
      variants = [];
    }
    variantsByKey = {};
    variants.forEach(function (v) {
      variantsByKey[optionKey(v.options)] = v;
    });
    optionCount = variants.length ? variants[0].options.length : 0;
  }

  function resolveVariant(selectedOptions) {
    return variantsByKey[optionKey(selectedOptions)] || null;
  }

  /* The variant that results from choosing `value` at `position` while
     keeping every other position at its current selection - used both for
     availability (Part 4) and for Pack merchandising below, since both
     questions are really "what variant would this candidate resolve to". */
  function findVariant(position, value, selectedOptions) {
    return variants.filter(function (v) {
      for (var p = 0; p < selectedOptions.length; p++) {
        var expected = p === position ? value : selectedOptions[p];
        if (v.options[p] !== expected) return false;
      }
      return true;
    })[0] || null;
  }

  /* Part 4: a value is available if it can pair with the OTHER positions'
     CURRENT selections to form at least one real, available variant - not
     whether the full current combination happens to be available. */
  function isValueAvailable(position, value, selectedOptions) {
    var variant = findVariant(position, value, selectedOptions);
    return !!(variant && variant.available);
  }

  /* ------------------------------------------------------------------------
     Pack merchandising: custom.pdp_pack_highlight_tag is the badge text and
     custom.pdp_pack_merchandising_style only selects its look (popular -> --pop,
     best -> --best, anything else -> --standard). Both are Variant-level, so the
     badge is refreshed from the variant the chip resolves to under the current
     selection. */
  var STANDARD_PILL_CLASS = 'we-pdp__opt-pill-badge--standard';

  function pillStyleClass(style) {
    if (style === 'popular') return 'we-pdp__opt-pill-badge--pop';
    if (style === 'best') return 'we-pdp__opt-pill-badge--best';
    return STANDARD_PILL_CLASS;
  }

  /* Pack Of chip badges. Every chip gets its merchandising tag and a "Save X%"
     badge:
       savings % = (qty x baseline - price) / (qty x baseline) x 100
     where baseline is the Pack of 1 variant with the SAME other options (Size,
     etc.), both prices are the variants' regular one-time priceCents (never a
     subscription price), qty is the first number in the Pack Of value text and
     the baseline is whichever Pack Of value has qty 1. The badge stays hidden
     when any input is missing or the result rounds to 0% or less. Which of the
     two badges is visible is decided in CSS from the purchase mode. */
  function packQuantity(value) {
    var match = /\d+(?:\.\d+)?/.exec(value || '');
    var qty = match ? parseFloat(match[0]) : 0;
    return qty > 0 ? qty : 0;
  }

  function packSavingsPercent(position, value, selectedOptions) {
    var qty = packQuantity(value);
    if (qty <= 1) return 0;

    var baseValue = null;
    optionGroupEls(position).forEach(function (el) {
      if (baseValue === null && packQuantity(el.dataset.wepOptValue) === 1) baseValue = el.dataset.wepOptValue;
    });
    if (baseValue === null) return 0;

    var pack = findVariant(position, value, selectedOptions);
    var base = findVariant(position, baseValue, selectedOptions);
    var packCents = pack && pack.pricing ? Number(pack.pricing.priceCents) : 0;
    var baseCents = base && base.pricing ? Number(base.pricing.priceCents) : 0;
    if (!(packCents > 0) || !(baseCents > 0)) return 0;

    var full = qty * baseCents;
    if (packCents >= full) return 0;
    return Math.round(((full - packCents) / full) * 100);
  }

  function applyPackPillBadge(pill, position, value, selectedOptions) {
    var savingsEl = qs('.we-pdp__opt-pill-badge--savings', pill);
    if (savingsEl) {
      var percent = packSavingsPercent(position, value, selectedOptions);
      savingsEl.textContent = percent > 0 ? 'Save ' + percent + '%' : '';
      savingsEl.hidden = !(percent > 0);
    }

    var merchEl = qs('.we-pdp__opt-pill-badge--merch', pill);
    if (merchEl) {
      var variant = findVariant(position, value, selectedOptions);
      var tag = (variant && variant.merch && variant.merch.tag) || '';
      merchEl.textContent = tag;
      merchEl.hidden = !tag;
      merchEl.className = 'we-pdp__opt-pill-badge we-pdp__opt-pill-badge--merch ' + pillStyleClass(variant && variant.merch && variant.merch.style);
    }
  }

  /* ------------------------------------------------------------------------
     Selected-option state
     ------------------------------------------------------------------------
     One value per option position (0-based, matching variant.options
     order = Shopify's option1/2/3 order). Read from whichever element the
     Liquid template already rendered as selected/active per position -
     that is already correct on load (?variant=, or the safe fallback for
     an invalid one), so there is nothing to parse from the URL here.
     ------------------------------------------------------------------------ */
  function optionGroupEls(position) {
    return qsa('[data-wep-option-position="' + (position + 1) + '"][data-wep-opt-value]');
  }

  function isSelectedAttr(el) {
    return el.getAttribute('aria-pressed') === 'true';
  }

  function readSelectedOptions() {
    var selected = [];
    for (var p = 0; p < optionCount; p++) {
      var el = optionGroupEls(p).filter(isSelectedAttr)[0];
      selected.push(el ? el.dataset.wepOptValue : null);
    }
    return selected;
  }

  var selectedOptions = [];
  var lastVariantId = null;

  /* ------------------------------------------------------------------------
     DOM sync
     ------------------------------------------------------------------------ */
  function setText(el, value) {
    if (el) el.textContent = value;
  }

  function applyOptionUI() {
    for (var p = 0; p < optionCount; p++) {
      var els = optionGroupEls(p);
      var groupRoot = qs('.we-pdp__opt[data-wep-option-position="' + (p + 1) + '"]');
      var isPackGroup = !!(groupRoot && groupRoot.classList.contains('we-pdp__opt--pack'));

      els.forEach(function (el) {
        var value = el.dataset.wepOptValue;
        var selected = value === selectedOptions[p];
        var available = isValueAvailable(p, value, selectedOptions);

        el.classList.toggle('we-pdp__opt-pill--active', selected);
        el.setAttribute('aria-pressed', String(selected));
        if (isPackGroup) {
          applyPackPillBadge(el, p, value, selectedOptions);
        }

        el.classList.toggle('we-pdp__opt-pill--unavailable', !available);
        if (available) {
          el.removeAttribute('aria-disabled');
        } else {
          el.setAttribute('aria-disabled', 'true');
        }
      });

      if (groupRoot) {
        setText(qs('.we-pdp__opt-label [data-wep-out]', groupRoot), selectedOptions[p]);
      }
    }
  }

  /* ------------------------------------------------------------------------
     History (Part 7/9): replaceState, not pushState.

     Every option click already updates the page in place with no
     navigation intent - the URL is a shareable/reloadable representation
     of the current variant, not a sequence of pages the user "went to".
     pushState would add a new history entry per click, so pressing Back
     would step through every intermediate combination a shopper tried
     before landing on the one they actually wanted, which reads as the
     Back button "not working" rather than as useful navigation. This
     matches how Shopify's own theme variant pickers behave (variant
     changes update the URL without growing browser history), so
     replaceState is the correct choice here, not a shortcut - explicitly
     not implementing pushState-per-click.
     ------------------------------------------------------------------------ */
  function updateUrl(variant) {
    if (!window.history || !window.history.replaceState) return;
    var url = new URL(window.location.href);
    url.searchParams.set('variant', String(variant.id));
    window.history.replaceState(window.history.state, '', url.toString());
  }

  /* Buy with Shop (fix): Shopify's own injected payment-button script
     re-renders {{ form | payment_button }} (we-pdp-buybox.liquid) for
     whichever variant its form's name="id" input currently holds, and it
     detects that by listening for a native "change" event on that input -
     the same signal a real variant <select> would fire. Updating the value
     alone would not be picked up, so a change event is dispatched on it
     here too. This is the only consumer of the hidden input; nothing else
     reads or writes data-wep-payment-variant-id. */
  function syncPaymentButton(variant) {
    var input = qs('[data-wep-payment-variant-id]');
    if (!input) return;
    input.value = String(variant.id);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* Seal Subscriptions fix: Seal is a sitewide Shopify app-embed (theme app
     extension - see config/settings_data.json, "seal-subscriptions/blocks/
     subscription-script-append"), not theme code, and its script scans the
     whole page for any <form action="/cart/add"> - the standard markup
     Liquid's {% form 'product' %} always emits, which is exactly what the
     Buy with Shop form above now renders. Before that form existed the New
     PDP had no real <form> at all (Add to Cart posts via fetch, never a
     <form> submit), so Seal had nothing on this page to attach to. Now it
     finds this one and injects its own "Select Frequency" purchase-options
     widget (.sealsubs-target-element) as the form's first child - the same
     way it already does, correctly, on the legacy PDP's own add-to-cart
     <form> (snippets/product-button.liquid). That widget duplicates the New
     PDP's own already-existing Subscribe & Save / one-time toggle above and
     was never asked for here.

     Confirmed empirically (live DOM, Seal's injected wrapper carries only a
     data-handle attribute) that Seal exposes no per-form opt-out marker
     this project can set in advance, and its script is Shopify-app-hosted,
     not a theme asset this repo controls - so there is no supported
     Liquid/config-level way to exclude one specific form. The smallest
     correct fix is to remove exactly what Seal injects, the moment it
     appears, scoped to ONLY this one form: a MutationObserver on
     #we-pdp-payment-form itself, never document-wide, so Seal's identical
     widget on the legacy PDP's own form is completely untouched.

     Removed outright, not CSS-hidden: Seal's widget also carries its own
     hidden purchase-option <input>s (selling plan/frequency), which would
     still submit alongside {{ form | payment_button }} even while only
     visually hidden - silently changing what Buy with Shop checks out as.
     Only .sealsubs-target-element is ever touched here; the payment
     button itself (.shopify-payment-button and everything inside it) is a
     sibling this never selects or removes. */
  function stripSealWidget(form) {
    qsa('.sealsubs-target-element', form).forEach(function (el) {
      el.remove();
    });
  }

  function guardPaymentForm() {
    var form = document.getElementById('we-pdp-payment-form');
    if (!form || form.__wepSealGuard) return;
    form.__wepSealGuard = true;
    stripSealWidget(form);
    new MutationObserver(function () {
      stripSealWidget(form);
    }).observe(form, { childList: true });
  }

  function dispatchVariantChange(variant) {
    syncPaymentButton(variant);
    document.dispatchEvent(new CustomEvent('wep:variant-change', {
      detail: { variantId: variant.id, variant: variant }
    }));
  }

  function selectVariant(variant) {
    applyOptionUI();
    if (!variant || variant.id === lastVariantId) return;
    lastVariantId = variant.id;
    updateUrl(variant);
    dispatchVariantChange(variant);
  }

  /* ------------------------------------------------------------------------
     Option value click (pill button)
     ------------------------------------------------------------------------
     Selecting a value that cannot form a real variant with the other
     groups' current selections is still reflected as chosen for that one
     group (the design has no existing disabled-state UX beyond the
     strike-through itself - see the final report's Part 5), but
     resolveVariant() then returns null, selectVariant() takes the "no
     variant" branch below, and nothing downstream (URL, event, Gallery)
     ever receives an invalid variant. The user's other groups re-evaluate
     availability against the new selection, same as any real Shopify
     variant picker.
     ------------------------------------------------------------------------ */
  function selectOptionValue(el) {
    var position = parseInt(el.dataset.wepOptionPosition, 10) - 1;
    if (position < 0 || position >= optionCount) return;
    selectedOptions[position] = el.dataset.wepOptValue;

    selectVariant(resolveVariant(selectedOptions));
  }

  /* ------------------------------------------------------------------------
     Delegated events - bound once
     ------------------------------------------------------------------------ */
  function onClick(event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var optValue = target.closest('[data-wep-opt-value]');
    if (optValue) {
      selectOptionValue(optValue);
      return;
    }

  }

  /* ------------------------------------------------------------------------
     Init / refresh
     ------------------------------------------------------------------------ */
  function refresh() {
    loadVariants();
    selectedOptions = readSelectedOptions();
    var variant = resolveVariant(selectedOptions);
    lastVariantId = variant ? variant.id : null;
    applyOptionUI();
    guardPaymentForm();
    if (variant) updateUrl(variant);
  }

  document.addEventListener('click', onClick);
  document.addEventListener('shopify:section:load', refresh);

  window.WePdpVariants = { bound: true, refresh: refresh };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh);
  } else {
    refresh();
  }
})();
